import Image from "next/image";
import { LearningArticle } from "../types/learningArticle";
import { formatDurationFromSeconds } from "../utils/formatDurationFromSeconds";
import { getImageURL } from "../utils/getImageURL";
import { normalizeDateString } from "../utils/normalizeDate";
import ArticleBlock from "./articleBlock";
import ArticleCard from "./articleCard";
import { AuthorBio } from "./authorBio";
import Block from "./block";
import Break from "./break";
import { RichText } from "./richText";
import SuggestedContent from "./suggestedContent";

export type ArticleProps = {
  article: LearningArticle;
  suggestedArticles?: LearningArticle[];
};

export function Article(props: ArticleProps) {
  const { article, suggestedArticles } = props;

  const {
    title,
    author,
    publishedAt,
    primaryImage,
    body,
    lede,
    readTime,
  }: LearningArticle = article;

  return (
    <>
      <article>
        {primaryImage && (
          <Block>
            <Image
              width={1200}
              height={800}
              className="mx-auto max-h-[540px] w-[1060px] rounded-3xl object-cover"
              src={getImageURL(primaryImage).url()}
              alt={primaryImage.alt ?? ""}
              quality={90}
            />
          </Block>
        )}

        <ArticleBlock flushBottom flushTop>
          <h1 className="text-head-2">
            <em>{title}</em>
          </h1>
          {author?.person && (
            <p className="text-body-2-medium text-gray">
              By {author?.person?.name}
              {author?.person?.title && `, ${author.person?.title}`}
            </p>
          )}
          <p className="text-body-3-medium text-gray">
            {normalizeDateString(publishedAt)}
          </p>
          {readTime && (
            <p className="text-body-3-medium text-gray">
              <em>{formatDurationFromSeconds(readTime, true)} read</em>
            </p>
          )}
        </ArticleBlock>

        {lede && (
          <>
            <ArticleBlock flushTop flushBottom>
              <RichText content={lede} />
            </ArticleBlock>
            <Block flushTop flushBottom>
              <Break />
            </Block>
          </>
        )}

        <ArticleBlock flushTop>
          <RichText content={body} />
        </ArticleBlock>

        {author && (
          <ArticleBlock flushBottom>
            <Break />
            <AuthorBio author={author} />
          </ArticleBlock>
        )}
      </article>

      <Block flushTop>
        <Break />
        <SuggestedContent>
          {suggestedArticles?.map((a: LearningArticle) => (
            <div key={`${a.title};${a.category}`} className="flex-1">
              <ArticleCard article={a} />
            </div>
          ))}
        </SuggestedContent>
      </Block>
    </>
  );
}

export default Article;
